// v1.5.0 · Inventarios para retail: logística de punta a punta, del centro de distribución a 8 sucursales (5 pantallas)
import React from 'react';
import { useTick, Count, Head, Live, Kpi, Status, useReduced } from './kit';

const STORES = [[90, 60, 'Norte'], [200, 40, 'Carcelén'], [330, 55, 'Cumbayá'], [420, 120, 'Tumbaco'],
  [400, 230, 'Valle'], [280, 260, 'Sur'], [140, 250, 'Quitumbe'], [60, 170, 'Centro']];
const CD = [240, 150];

/* 1 · Red logística */
export function RetRed() {
  const t = useTick(1600);
  const reduced = useReduced();
  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <Head title="Red logística" sub="Centro de distribución y 8 sucursales" right={<Live label="5 camiones en ruta" />} />
      <div className="grid grid-cols-3 gap-2">
        <Kpi label="Despachos hoy"><Count value={46 + t} /></Kpi>
        <Kpi label="Entregas a tiempo" tone="ok"><Count value={97.2} dec={1} suffix=" %" /></Kpi>
        <Kpi label="Kilómetros recorridos"><Count value={812 + t * 9} /></Kpi>
      </div>
      <div className="dash-panel relative min-h-0 flex-1 overflow-hidden">
        <svg viewBox="0 0 480 300" className="h-full w-full" role="img" aria-label="Rutas de camiones del centro de distribución a las sucursales">
          {STORES.map(([x, y, n], i) => {
            const d = `M${CD[0]} ${CD[1]} Q ${(CD[0] + x) / 2 + (i % 2 ? 30 : -30)} ${(CD[1] + y) / 2 + (i % 3 ? -20 : 20)} ${x} ${y}`;
            return (
              <g key={n}>
                <path d={d} fill="none" stroke="rgba(87,211,255,.25)" strokeWidth="1.5" className="flow" />
                {i % 2 === 0 && !reduced && (
                  <g><animateMotion dur={`${5 + i}s`} repeatCount="indefinite" path={d} rotate="auto" />
                    <rect x="-8" y="-4" width="16" height="8" rx="2" fill="#C7811F" stroke="#071925" strokeWidth="1.5" /></g>
                )}
                <circle cx={x} cy={y} r="9" fill="#0E2A3C" stroke="#57D3FF" strokeWidth="1.5" />
                <text x={x} y={y + 22} textAnchor="middle" fontSize="10.5" fill="#86A6B6">{n}</text>
              </g>
            );
          })}
          <rect x={CD[0] - 26} y={CD[1] - 16} width="52" height="32" rx="6" fill="#123247" stroke="#57D3FF" strokeWidth="2" />
          <rect x={CD[0] - 26} y={CD[1] - 16} width="52" height="32" rx="6" fill="none" stroke="#57D3FF" className="ring" />
          <text x={CD[0]} y={CD[1] + 4} textAnchor="middle" fontSize="11" fontWeight="800" fill="#DDF0F7">CD</text>
        </svg>
      </div>
    </div>
  );
}

/* 2 · Reposición automática */
const SKUS = ['Arroz 2 kg', 'Aceite 1 L', 'Leche 1 L', 'Detergente 3 kg', 'Atún 180 g', 'Azúcar 1 kg'];
export function RetReposicion() {
  const t = useTick(1100);
  const items = SKUS.map((n, i) => {
    const s = 100 - ((t * 9 + i * 29) % 100);
    return { n, s, low: s < 25 };
  });
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.5fr_1fr]">
      <div className="dash-panel flex min-h-0 flex-col gap-2 p-3">
        <Head title="Stock en Sucursal Norte" sub="La línea marca el mínimo de reposición" />
        <div className="relative grid min-h-0 flex-1 grid-cols-6 items-end gap-2 pt-2">
          <div className="pointer-events-none absolute inset-x-0 border-t border-dashed border-[#E9A23B]" style={{ bottom: `calc(${25}% * .82 + 26px)` }} />
          {items.map((it) => (
            <div key={it.n} className="flex h-full min-w-0 flex-col justify-end">
              <div className="flex h-[82%] items-end"><div className="bar-live w-full rounded-t-[4px]" style={{ height: '100%', transform: `scaleY(${it.s / 100})`, background: it.low ? '#E9A23B' : '#2E9BD6' }} /></div>
              <div className="dim mt-1.5 h-[26px] text-center text-[10px] leading-tight">{it.n}</div>
            </div>
          ))}
        </div>
      </div>
      <ul className="dash-panel flex min-h-0 flex-col gap-2 p-3" aria-label="Pedidos de reposición">
        <li className="dim text-[11px] font-semibold">Pedidos generados solos</li>
        {items.filter((i) => i.low).concat(items.filter((i) => !i.low)).slice(0, 4).map((it) => (
          <li key={it.n} className="rise flex items-center justify-between gap-2 text-[12px]">
            <span className="truncate font-semibold">{it.n}</span>
            {it.low ? <Status tone="warn">Pedido al CD</Status> : <Status tone="ok">Stock suficiente</Status>}
          </li>
        ))}
        <li className="mt-auto rounded-md bg-[rgba(46,155,214,.12)] p-2 text-[11.5px] leading-snug">Al cruzar el mínimo, el sistema arma el pedido y lo agenda en el próximo camión.</li>
      </ul>
    </div>
  );
}

/* 3 · Seguimiento de despachos */
const STAGES = ['Preparado', 'Cargado', 'En tránsito', 'Entregado'];
export function RetDespachos() {
  const t = useTick(1400);
  const rows = [0, 1, 2, 3, 4].map((k) => ({ id: 3120 + k, to: STORES[(k * 3) % 8][2], st: Math.min(3, Math.max(0, ((t + k * 2) % 9) - 2)) }));
  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <Head title="Seguimiento de despachos" sub="Cada guía con su estado en tiempo real" right={<Live />} />
      <div className="dash-panel min-h-0 flex-1 overflow-hidden">
        <div className="dim grid grid-cols-[90px_1fr] gap-2 border-b border-white/10 px-3 py-2 text-[11px] font-semibold sm:grid-cols-[110px_110px_1fr]">
          <span>Guía</span><span className="hidden sm:block">Destino</span><span>Estado</span>
        </div>
        {rows.map((r) => (
          <div key={r.id} className="grid grid-cols-[90px_1fr] items-center gap-2 border-b border-white/5 px-3 py-2.5 text-[12px] sm:grid-cols-[110px_110px_1fr]">
            <span className="num font-semibold">GR-{r.id}</span>
            <span className="dim hidden sm:block">{r.to}</span>
            <div className="flex items-center gap-1">
              {STAGES.map((s, i) => (
                <div key={s} className="flex flex-1 flex-col gap-1">
                  <div className={`h-1.5 rounded transition-colors duration-500 ${i <= r.st ? (r.st === 3 ? 'bg-[#4FBF6A]' : 'bg-[#2E9BD6]') : 'bg-white/10'}`} />
                  <span className={`hidden truncate text-[10px] sm:block ${i === r.st ? 'text-white' : 'dim'}`}>{s}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* 4 · Picking en bodega */
const PICK = 'M26 240 L 26 34 L 98 34 L 98 240 L 170 240 L 170 34 L 242 34 L 242 240 L 314 240 L 314 34 L 368 34';
const LINES = ['Pasillo 1, A-04: Arroz 2 kg x 12', 'Pasillo 2, B-11: Aceite 1 L x 24', 'Pasillo 3, C-02: Leche 1 L x 36',
  'Pasillo 4, D-08: Atún 180 g x 48', 'Pasillo 5, E-03: Azúcar 1 kg x 20'];
export function RetPicking() {
  const t = useTick(1200);
  const reduced = useReduced();
  const done = t % 6;
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.4fr_1fr]">
      <div className="dash-panel min-h-0 overflow-hidden p-2">
        <svg viewBox="0 0 380 280" className="h-full w-full" role="img" aria-label="Recorrido óptimo de picking por los pasillos de la bodega">
          {[0, 1, 2, 3, 4].map((i) => (
            <g key={i}>{[0, 1, 2, 3, 4, 5].map((j) => <rect key={j} x={44 + i * 72} y={46 + j * 32} width="36" height="24" rx="3" fill={i === done - 1 && j === 2 ? 'rgba(79,191,106,.5)' : '#123247'} stroke="rgba(87,211,255,.2)" style={{ transition: 'fill 400ms' }} />)}</g>
          ))}
          <path d={PICK} fill="none" stroke="rgba(87,211,255,.45)" strokeWidth="2" strokeDasharray="4 6" />
          <circle r="7" fill="#C7811F" stroke="#071925" strokeWidth="2">{!reduced && <animateMotion dur="7.2s" repeatCount="indefinite" path={PICK} />}</circle>
          <text x="190" y="272" textAnchor="middle" fontSize="10" fill="#86A6B6">Ruta más corta calculada por el sistema</text>
        </svg>
      </div>
      <div className="dash-panel flex min-h-0 flex-col gap-2 p-3">
        <Head title="Orden PK-882" sub="Para Sucursal Valle" right={<Status tone={done >= 5 ? 'ok' : 'info'}>{done >= 5 ? 'Lista' : 'En picking'}</Status>} />
        {LINES.map((l, i) => (
          <div key={l} className={`flex items-center gap-2 text-[11.5px] transition-opacity duration-300 ${i < done ? '' : 'opacity-60'}`}>
            <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded text-[9px] ${i < done ? 'bg-[#4FBF6A] text-[#04121C]' : 'border border-white/20'}`}>{i < done ? '✓' : ''}</span>
            <span className={i < done ? 'line-through decoration-white/30' : ''}>{l}</span>
          </div>
        ))}
        <div className="mt-auto h-1.5 overflow-hidden rounded bg-white/10"><div className="bar-live-x h-full rounded bg-[#4FBF6A]" style={{ transform: `scaleX(${done / 5})` }} /></div>
      </div>
    </div>
  );
}

/* 5 · Cobertura y rotación */
const CATS = [['Abarrotes', 18, 'A'], ['Lácteos', 6, 'A'], ['Limpieza', 24, 'B'], ['Bebidas', 11, 'A'], ['Cuidado personal', 32, 'C'], ['Snacks', 9, 'B']];
export function RetCobertura() {
  const t = useTick(2000);
  const W = (d) => Math.min(1, d / 40);
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.5fr_1fr]">
      <div className="dash-panel flex min-h-0 flex-col gap-2.5 p-3">
        <Head title="Días de cobertura por categoría" sub="Meta entre 7 y 21 días" />
        <div className="relative flex flex-1 flex-col justify-around">
          <div className="pointer-events-none absolute inset-y-0 rounded bg-[rgba(79,191,106,.08)]" style={{ left: `calc(110px + (100% - 150px) * ${7 / 40})`, width: `calc((100% - 150px) * ${14 / 40})` }} />
          {CATS.map(([n, d], i) => {
            const v = d + ((t + i) % 3) - 1;
            const tone = v < 7 ? '#E9A23B' : v > 21 ? '#8B6BE0' : '#2E9BD6';
            return (
              <div key={n} className="relative flex items-center gap-2 text-[11.5px]">
                <span className="w-[102px] shrink-0 truncate">{n}</span>
                <div className="h-3.5 flex-1"><div className="bar-live-x h-full rounded-r-[4px]" style={{ background: tone, transform: `scaleX(${W(v)})` }} /></div>
                <span className="num w-10 shrink-0 text-right font-semibold">{v} d</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex min-h-0 flex-col gap-2">
        <Kpi label="Quiebres de stock este mes" tone="ok"><Count value={-40} suffix=" %" /></Kpi>
        <Kpi label="Rotación promedio"><Count value={4.6} dec={1} suffix=" veces/mes" /></Kpi>
        <div className="dash-panel p-3 text-[11.5px] leading-snug">
          <div className="mb-1.5 flex flex-wrap gap-1.5"><Status tone="warn">Bajo meta, naranja</Status><Status tone="info">En meta, azul</Status>
            <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(139,107,224,.2)] px-2 py-0.5 text-[10.5px] font-semibold text-[#C3B2F5]">◆ Sobrestock, violeta</span></div>
          <span className="dim">Ante sobrestock, el sistema sugiere promociones para liberar capital.</span>
        </div>
      </div>
    </div>
  );
}

export const retailScreens = [
  { t: 'Red logística', C: RetRed }, { t: 'Reposición', C: RetReposicion }, { t: 'Despachos', C: RetDespachos },
  { t: 'Picking', C: RetPicking }, { t: 'Cobertura', C: RetCobertura },
];
